import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { buildApiUrl, API_CONFIG } from '../config/api';
import {
  CubeIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  PlayIcon,
  EyeIcon,
  CommandLineIcon,
  ServerIcon,
  ClockIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

interface KubernetesPod {
  name: string;
  namespace: string;
  status: string;
  ready: string;
  age: string;
}

interface KubernetesEvent {
  event_type: string;
  namespace: string;
  resource_type: string;
  resource_name: string;
  user: string | null;
  action: string;
  details: string | null;
  created_at: string;
}

interface TeleportSession {
  session_id: string;
  username: string;
  session_type: string;
  server_addr: string;
  client_addr: string;
  start_time: string;
  duration: string;
  cluster_name?: string;
  pod_name?: string;
  pod_namespace?: string;
  container_name?: string;
  command?: string;
}



interface TeleportCluster {
  name: string;
  value: string;
  selected?: boolean;
}

const Kubernetes: React.FC = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [switchingCluster, setSwitchingCluster] = useState(false);

  // Kubernetes data
  const [pods, setPods] = useState<KubernetesPod[]>([]);
  const [events, setEvents] = useState<KubernetesEvent[]>([]);
  const [namespaces, setNamespaces] = useState<string[]>([]);
  const [nodes, setNodes] = useState<any[]>([]);
  const [selectedNamespace, setSelectedNamespace] = useState('default');

  // Teleport data
  const [teleportClusters, setTeleportClusters] = useState<TeleportCluster[]>([]);
  const [selectedCluster, setSelectedCluster] = useState<string>('');
  const [activeSessions, setActiveSessions] = useState<TeleportSession[]>([]);
  const [teleportUsers, setTeleportUsers] = useState<TeleportUser[]>([]);

  interface TeleportUser {
    name: string;
    is_locked: boolean;
    created_by: string;
    created_at: string;
  }

  const tabs = [
    { id: 'overview', name: 'Overview', icon: EyeIcon },
    { id: 'pods', name: 'Pods', icon: CubeIcon },
    { id: 'events', name: 'Events', icon: ExclamationTriangleIcon },
    { id: 'sessions', name: 'Active Sessions', icon: UserGroupIcon },
    { id: 'users', name: 'Users', icon: UserGroupIcon },
  ];

  useEffect(() => {
    fetchTeleportClusters();
  }, []);

  useEffect(() => {
    if (selectedCluster) {
      fetchData();
    }
    
    // Set up auto-refresh every 30 seconds
    const interval = setInterval(() => {
      if (selectedCluster) {
        fetchData();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [activeTab, selectedNamespace, selectedCluster]);

  const switchCluster = async (clusterName: string) => {
    if (switchingCluster) {
      console.log('Already switching cluster, ignoring request');
      return;
    }

    setSwitchingCluster(true);
    setError(null);

    try {
      console.log(`Switching to cluster: ${clusterName}`);
      const response = await fetch(buildApiUrl(`${API_CONFIG.ENDPOINTS.KUBERNETES_CLUSTERS}/${clusterName}/switch`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        console.log(`Successfully switched to cluster: ${clusterName}`, result);
        
        // Clear Kubernetes cache to ensure fresh data
        try {
          await fetch(buildApiUrl('/api/kubernetes/cache/clear'), {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
          console.log('Cleared Kubernetes cache');
        } catch (cacheError) {
          console.warn('Failed to clear cache:', cacheError);
        }
        
        // Clear existing data to show loading state
        setPods([]);
        setEvents([]);
        setNamespaces([]);
        setNodes([]);
        setActiveSessions([]);
        // Update selected cluster state
        setSelectedCluster(clusterName);
        // Refresh data for new cluster with explicit cluster name
        await fetchData(clusterName);
      } else {
        const errorData = await response.json();
        console.error(`Failed to switch to cluster: ${clusterName}`, errorData);
        setError(`Failed to switch to cluster: ${errorData.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error switching cluster:', error);
      setError(`Error switching cluster: ${error}`);
    } finally {
      setSwitchingCluster(false);
    }
  };

  const fetchTeleportClusters = async () => {
    try {
      console.log('Fetching Teleport clusters...');
      const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.KUBERNETES_CLUSTERS), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      if (response.ok) {
        const data = await response.json();
        console.log('Response data:', data);
        
        // Go backend format: { success: true, data: { clusters: [...] } }
        if (data.success && data.data && data.data.clusters && Array.isArray(data.data.clusters)) {
          const clusters = data.data.clusters.map((cluster: any) => ({
            name: cluster.name,
            value: cluster.name,
            selected: cluster.selected || false
          }));
          console.log('Processed clusters:', clusters);
          setTeleportClusters(clusters);
          
          // Set selected cluster if not already set
          if (!selectedCluster && clusters.length > 0) {
            const defaultCluster = clusters.find((c: TeleportCluster) => c.selected) || clusters[0];
            console.log('Setting default cluster:', defaultCluster);
            setSelectedCluster(defaultCluster.value);
          }
        } else {
          console.log('No clusters found in response or invalid format');
        }
      } else {
        console.error('Response not ok:', response.status, response.statusText);
      }
    } catch (err) {
      console.error('Error fetching Teleport clusters:', err);
    }
  };

  const fetchData = async (explicitCluster?: string) => {
    const clusterToUse = explicitCluster || selectedCluster;
    if (!clusterToUse) {
      console.log('No cluster selected, skipping data fetch');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const promises = [];

      // Fetch data based on active tab
      switch (activeTab) {
        case 'overview':
          promises.push(fetchPods(clusterToUse));
          promises.push(fetchNamespaces(clusterToUse));
          promises.push(fetchNodes(clusterToUse));
          break;
        case 'pods':
          promises.push(fetchPods(clusterToUse));
          promises.push(fetchNamespaces(clusterToUse));
          break;
        case 'events':
          promises.push(fetchEvents());
          break;
        case 'sessions':
          promises.push(fetchActiveSessions());
          break;
        case 'users':
          promises.push(fetchTeleportUsers());
          break;
      }

      // Use Promise.allSettled to handle individual failures gracefully
      const results = await Promise.allSettled(promises);
      
      // Check if all promises were rejected
      const allRejected = results.every(result => result.status === 'rejected');
      if (allRejected) {
        setError('Failed to fetch data from all services');
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPods = async (clusterName?: string) => {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      const clusterToUse = clusterName || selectedCluster;
      if (clusterToUse) {
        params.append('cluster', clusterToUse);
      }
      if (activeTab !== 'overview' && selectedNamespace) {
        params.append('namespace', selectedNamespace);
      }
      
      const queryString = params.toString();
      const url = buildApiUrl(`${API_CONFIG.ENDPOINTS.KUBERNETES_PODS}${queryString ? `?${queryString}` : ''}`);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Go backend format: { success: true, data: { pods: [...] } }
        setPods(data.data?.pods || []);
      }
    } catch (err) {
      console.error('Error fetching pods:', err);
    }
  };

  const fetchEvents = async () => {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (selectedCluster) {
        params.append('cluster', selectedCluster);
      }
      
      const queryString = params.toString();
      const url = buildApiUrl(`${API_CONFIG.ENDPOINTS.KUBERNETES_EVENTS}${queryString ? `?${queryString}` : ''}`);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Go backend format: { success: true, data: { events: [...] } }
        setEvents(data.data?.events || []);
      }
    } catch (err) {
      console.error('Error fetching events:', err);
    }
  };

  const fetchNamespaces = async (clusterName?: string) => {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      const clusterToUse = clusterName || selectedCluster;
      if (clusterToUse) {
        params.append('cluster', clusterToUse);
      }
      
      const queryString = params.toString();
      const url = buildApiUrl(`${API_CONFIG.ENDPOINTS.KUBERNETES_NAMESPACES}${queryString ? `?${queryString}` : ''}`);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Go backend format: { success: true, data: { namespaces: [...] } }
        if (data.data && data.data.namespaces && Array.isArray(data.data.namespaces)) {
          // Extract just the names from the namespace objects
          const namespaceNames = data.data.namespaces.map((ns: any) => ns.name);
          setNamespaces(namespaceNames);
        } else {
          setNamespaces([]);
        }
      }
    } catch (err) {
      console.error('Error fetching namespaces:', err);
      setNamespaces([]);
    }
  };

  const fetchNodes = async (clusterName?: string) => {
    try {
      // Build query parameters
      const params = new URLSearchParams();
      const clusterToUse = clusterName || selectedCluster;
      if (clusterToUse) {
        params.append('cluster', clusterToUse);
      }
      
      const queryString = params.toString();
      const url = buildApiUrl(`${API_CONFIG.ENDPOINTS.KUBERNETES_NODES}${queryString ? `?${queryString}` : ''}`);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Go backend format: { success: true, data: { nodes: [...] } }
        setNodes(data.data?.nodes || []);
      }
    } catch (err) {
      console.error('Error fetching nodes:', err);
      setNodes([]);
    }
  };

  const fetchActiveSessions = async () => {
    try {
      // Add cluster filter if a cluster is selected
      const clusterParam = selectedCluster ? `?cluster=${encodeURIComponent(selectedCluster)}` : '';
      const response = await fetch(buildApiUrl(`${API_CONFIG.ENDPOINTS.KUBERNETES_SESSIONS}${clusterParam}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Go backend format: { success: true, data: { sessions: [...] } }
        setActiveSessions(data.data?.sessions || []);
      }
    } catch (err) {
      console.error('Error fetching active sessions:', err);
    }
  };



  const fetchTeleportUsers = async () => {
    try {
      const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.KUBERNETES_USERS), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data && data.data.users) {
          setTeleportUsers(data.data.users);
        }
      } else {
        console.error('Failed to fetch Teleport users');
      }
    } catch (error) {
      console.error('Error fetching Teleport users:', error);
    }
  };

  const blockUser = async (username: string, action: 'block' | 'unblock' = 'block') => {
    try {
      const response = await fetch(buildApiUrl(`${API_CONFIG.ENDPOINTS.KUBERNETES_USERS}/${username}/block?action=${action}`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        await fetchActiveSessions();
        await fetchTeleportUsers(); // Refresh users list
        alert(`User ${username} has been ${action}ed successfully`);
      } else {
        const errorData = await response.json();
        alert(`Failed to ${action} user: ${errorData.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error(`Error ${action}ing user:`, err);
      alert(`Error ${action}ing user: ${err}`);
    }
  };



  const getSessionTypeColor = (sessionType: string) => {
    switch (sessionType.toLowerCase()) {
      case 'k8s':
        return 'text-blue-600 bg-blue-100';
      case 'ssh':
        return 'text-green-600 bg-green-100';
      case 'db':
        return 'text-purple-600 bg-purple-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getEventTypeColor = (eventType: string) => {
    switch (eventType.toLowerCase()) {
      case 'warning':
        return 'text-yellow-600 bg-yellow-100';
      case 'error':
        return 'text-red-600 bg-red-100';
      case 'normal':
        return 'text-green-600 bg-green-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  if (loading && activeTab === 'overview') {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Kubernetes Monitoring</h1>
          <p className="mt-2 text-gray-600">
            Monitor Kubernetes clusters through Teleport with real-time session tracking and security controls
          </p>
        </div>

        {/* Cluster Selection */}
        {teleportClusters.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <h2 className="text-lg font-semibold text-gray-900">Cluster Selection</h2>
                {switchingCluster && (
                  <div className="flex items-center space-x-2 text-sm text-blue-600">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <span>Switching...</span>
                  </div>
                )}
              </div>
              <select
                value={selectedCluster}
                onChange={async (e) => {
                  const newCluster = e.target.value;
                  await switchCluster(newCluster);
                }}
                disabled={switchingCluster}
                className={`border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  switchingCluster ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {teleportClusters.map((cluster) => (
                  <option key={cluster.value} value={cluster.value}>
                    {cluster.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="h-5 w-5" />
                  <span>{tab.name}</span>
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
                        {activeTab === 'overview' && (
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Cluster Overview</h3>
                
                {selectedCluster ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {/* Total Nodes */}
                    <div className="bg-white p-6 rounded-lg shadow border">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <ServerIcon className="h-8 w-8 text-blue-600" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-gray-500">Total Nodes</p>
                          <p className="text-2xl font-semibold text-gray-900">{nodes.length}</p>
                        </div>
                      </div>
                    </div>

                    {/* Total Pods */}
                    <div className="bg-white p-6 rounded-lg shadow border">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <CubeIcon className="h-8 w-8 text-green-600" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-gray-500">Total Pods</p>
                          <p className="text-2xl font-semibold text-gray-900">{pods.length}</p>
                        </div>
                      </div>
                    </div>

                    {/* Running Pods */}
                    <div className="bg-white p-6 rounded-lg shadow border">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <CheckCircleIcon className="h-8 w-8 text-green-600" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-gray-500">Running Pods</p>
                          <p className="text-2xl font-semibold text-gray-900">
                            {pods.filter(pod => pod.status === 'Running').length}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Total Namespaces */}
                    <div className="bg-white p-6 rounded-lg shadow border">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <ShieldCheckIcon className="h-8 w-8 text-purple-600" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-gray-500">Namespaces</p>
                          <p className="text-2xl font-semibold text-gray-900">{namespaces.length}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <CubeIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No cluster selected</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Please select a cluster from the dropdown above to view overview information.
                    </p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'pods' && (
              <div>
                {/* Namespace Selection */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Namespace</label>
                  <select
                    value={selectedNamespace}
                    onChange={(e) => setSelectedNamespace(e.target.value)}
                    className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {namespaces.map((namespace) => (
                      <option key={namespace} value={namespace}>
                        {namespace}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Pods Table */}
                {loading ? (
                  <LoadingSpinner />
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Namespace</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ready</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Age</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {pods.map((pod) => (
                          <tr key={`${pod.namespace}-${pod.name}`} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{pod.name}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{pod.namespace}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                pod.status === 'Running' ? 'text-green-600 bg-green-100' : 'text-red-600 bg-red-100'
                              }`}>
                                {pod.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {pod.ready}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{pod.age}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'events' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Teleport Audit Log</h3>
                {/* Events Table */}
                {loading ? (
                  <LoadingSpinner />
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 table-fixed">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Type</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">Namespace</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">Resource</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">User</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">Action</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">Time</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {events.map((event, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-4 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getEventTypeColor(event.event_type)}`}>
                                {event.event_type}
                              </span>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{event.namespace}</td>
                            <td className="px-4 py-4 text-sm text-gray-900 break-words">
                              <div className="truncate" title={`${event.resource_type} ${event.resource_name}`}>
                                {event.resource_type} {event.resource_name}
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{event.user || 'N/A'}</td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{event.action}</td>
                            <td className="px-4 py-4 text-sm text-gray-900">
                              <div className="break-all whitespace-pre-wrap max-h-20 overflow-y-auto" title={event.details || ''}>
                                {event.details}
                              </div>
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                              {new Date(event.created_at).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'sessions' && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Active Teleport Sessions</h3>
                {loading ? (
                  <LoadingSpinner />
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">K8s Cluster</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Namespace</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pod Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Init Command</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {activeSessions.length === 0 ? (
                          <tr>
                            <td colSpan={8} className="px-6 py-4 text-center text-gray-500">
                              No active Teleport sessions
                            </td>
                          </tr>
                        ) : (
                          activeSessions.map((session) => (
                            <tr key={session.session_id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                <div className="flex items-center">
                                  <UserGroupIcon className="h-4 w-4 text-gray-400 mr-2" />
                                  {session.username}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getSessionTypeColor(session.session_type)}`}>
                                  {session.session_type}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {session.cluster_name || 'N/A'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {session.pod_namespace || 'N/A'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                <div className="flex items-center">
                                  <CubeIcon className="h-4 w-4 text-gray-400 mr-2" />
                                  {session.pod_name || 'N/A'}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                <div className="flex items-center">
                                  <CommandLineIcon className="h-4 w-4 text-gray-400 mr-2" />
                                  <code className="bg-gray-100 px-2 py-1 rounded text-xs">
                                    {session.command || 'N/A'}
                                  </code>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                <div className="flex items-center">
                                  <ClockIcon className="h-4 w-4 text-gray-400 mr-2" />
                                  {session.duration}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => blockUser(session.username)}
                                    className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                                  >
                                    <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                                    Block User
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && (
              <div className="space-y-6">
                <div className="bg-white shadow rounded-lg">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">Teleport Users</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Manage Teleport users (excluding bot users)
                    </p>
                  </div>
                  
                  {loading ? (
                    <div className="flex justify-center items-center py-12">
                      <LoadingSpinner />
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created By</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created At</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {teleportUsers.length === 0 ? (
                            <tr>
                              <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                                No Teleport users found
                              </td>
                            </tr>
                          ) : (
                            teleportUsers.map((user) => (
                              <tr key={user.name} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  <div className="flex items-center">
                                    <UserGroupIcon className="h-4 w-4 text-gray-400 mr-2" />
                                    {user.name}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                    user.is_locked 
                                      ? 'bg-red-100 text-red-800' 
                                      : 'bg-green-100 text-green-800'
                                  }`}>
                                    {user.is_locked ? 'Blocked' : 'Active'}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {user.created_by}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {new Date(user.created_at).toLocaleString()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                  <div className="flex space-x-2">
                                    {user.is_locked ? (
                                      <button
                                        onClick={() => blockUser(user.name, 'unblock')}
                                        className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                      >
                                        <CheckCircleIcon className="h-3 w-3 mr-1" />
                                        Unblock
                                      </button>
                                    ) : (
                                      <button
                                        onClick={() => blockUser(user.name, 'block')}
                                        className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                                      >
                                        <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                                        Block
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default Kubernetes;
